import { Immutable } from "immer";
import React from "react";
import { ErrorBoundary } from "tsi-common-react/src/components/ErrorBoundary/index";

import type { templates } from "@reactivated";

// Client-only loader for the Product Line Collection (PLC) configurator. The
// configurator lives inside the reactivated document (rendered by
// ProductLineCollectionPageTemplate) rather than mounted via a separate
// createRoot/dynamicPlaceComponent, but it is an interactive Redux app whose
// dependency tree is not SSR-safe, so it is lazy-imported and gated to
// client-only paint: the server and the first hydration render both produce
// null (no mismatch), and the real configurator mounts after hydration. This
// also folds in the old factory's `await readyStateComplete` / `await
// rehydratingStore`: the app now paints after hydration, and the store Provider
// already wraps the document tree.
const PLCConfiguratorAppInner = React.lazy(
    () => import("./PLCConfiguratorAppInner"),
);

type IProps = Immutable<{
    // The PLC configurator payload, sourced from the reactivated document prop
    // (ProductLineCollectionPageTemplate's props.plc_configurator, carried to
    // the client via __PRELOADED_PROPS__) rather than the mount div's data-*
    // attributes the old imperative island read off the DOM.
    configurator: templates.ProductLineCollectionPageTemplate["plc_configurator"];
    // The page's serialized CMS payload (props.cms_json). Decoded client-side in
    // the inner app to derive the upsell modal, delivery copy, and shipping
    // lead/icon -- previously JSON.parsed off #product-detail-container[data-cms].
    cmsJson: string;
}>;

// The ErrorBoundary wraps Suspense (not just the inner app) so it catches both
// a lazy chunk-load failure -- which throws at the Suspense site -- and any
// render error from the configurator. The app renders inside the document's
// single React root, so an uncaught failure here would otherwise blank the
// whole document; the boundary keeps the blast radius scoped to the mount div.
export const PLCConfiguratorApp = (props: IProps): React.ReactNode => {
    const [isHydrated, setIsHydrated] = React.useState(false);
    React.useEffect(() => setIsHydrated(true), []);
    if (!isHydrated) {
        return null;
    }
    return (
        <ErrorBoundary>
            <React.Suspense fallback={null}>
                <PLCConfiguratorAppInner
                    configurator={props.configurator}
                    cmsJson={props.cmsJson}
                />
            </React.Suspense>
        </ErrorBoundary>
    );
};
